import { formatValue } from "@/domain/calibration-params";
import {
  matrixCol,
  type MetricDisplaySpec,
  type MetricParamValue,
} from "@/domain/metrics-params";

const METRICS_ACCURACY_FALLBACK = 4;

// Fixed geometry rather than a fluid viewBox: the card already scrolls
// horizontally for the value tables, and stretching would scale the stroke
// widths along with the box widths.
const PAD_LEFT = 46;
const PAD_RIGHT = 8;
const PAD_TOP = 8;
// No x axis of its own: the value table sits directly below with the same four
// numbered columns, and a second row of 1-2-3-4 that cannot line up with the
// table's (which is offset by its row-label column) reads as a different scale.
const PAD_BOTTOM = 4;
const SLOT_W = 44;
const BOX_W = 20;
const CAP_W = 11;
const PLOT_H = 84;

interface ChannelBox {
  col: number;
  /** Five quantiles, ascending: 0 / 25 / 50 / 75 / 100. */
  q: [number, number, number, number, number];
}

/** Channels whose five quantiles all read — a null anywhere (a non-finite bot
 * output) makes the whole distribution unplottable, so that channel is left
 * blank rather than drawn from a partial summary. */
function channelBoxes(values: (number | null)[], spec: MetricDisplaySpec): ChannelBox[] {
  const boxes: ChannelBox[] = [];
  for (let col = 0; col < spec.cols; col++) {
    const q = matrixCol(values, spec, col);
    if (q.length === 5 && q.every((v) => v != null)) {
      boxes.push({ col, q: q as ChannelBox["q"] });
    }
  }
  return boxes;
}

/** Distribution of one quantile metric: a box per channel, on the y-scale of
 * this metric's own run so the four channels are comparable with each other —
 * never across metrics, which is why every plot prints its own endpoints.
 *
 * One measure across four channels, so one hue and no legend; the previous
 * recording, when shown, is a dashed outline rather than a second colour, and
 * the exact numbers sit in the table directly below.
 */
export function MetricsBoxPlot({
  spec,
  param,
  compare,
}: {
  spec: MetricDisplaySpec;
  param: MetricParamValue;
  compare?: MetricParamValue;
}) {
  const boxes = channelBoxes(param.values, spec);
  if (boxes.length === 0) return null;
  const previous = compare ? channelBoxes(compare.values, spec) : [];

  const all = [...boxes, ...previous].flatMap((b) => b.q);
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  // A run with no spread at all (a boat that never moved reads all zeros) still
  // has to draw; a nominal domain lands every mark mid-plot instead of NaN.
  const span = hi - lo || 1;
  const y = (value: number) => PAD_TOP + PLOT_H - ((value - lo) / span) * PLOT_H;
  const x = (col: number) => PAD_LEFT + col * SLOT_W + SLOT_W / 2;

  const width = PAD_LEFT + spec.cols * SLOT_W + PAD_RIGHT;
  const height = PAD_TOP + PLOT_H + PAD_BOTTOM;
  const accuracy = param.accuracy ?? METRICS_ACCURACY_FALLBACK;
  const format = (v: number) => formatValue(v, accuracy, param.chpDataType);

  const summary = (b: ChannelBox) =>
    `${b.col + 1}: ` +
    b.q.map((v, i) => `${spec.rowLabels?.[i] ?? i} ${format(v)}`).join(" · ");

  const boxTop = (b: ChannelBox) => y(b.q[3]);
  const boxHeight = (b: ChannelBox) => Math.max(1, y(b.q[1]) - y(b.q[3]));

  return (
    <div className="min-w-0">
      {compare && (
        <div className="flex items-center gap-3 text-[10px] leading-none text-muted-foreground mb-1">
          <span className="inline-flex items-center gap-1">
            <span aria-hidden className="h-2.5 w-4 rounded-[2px] border border-chart-2 bg-chart-2/25" />
            this recording
          </span>
          <span className="inline-flex items-center gap-1">
            <span aria-hidden className="h-2.5 w-4 rounded-[2px] border border-dashed border-muted-foreground" />
            previous
          </span>
        </div>
      )}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${spec.label}: ${boxes.map(summary).join("; ")}`}
        className="mb-1"
      >
        {/* Endpoints only, no gridlines — the exact numbers are the table below. */}
        <text
          x={PAD_LEFT - 6}
          y={y(hi) + 3}
          textAnchor="end"
          className="fill-muted-foreground text-[9px] tabular-nums"
        >
          {format(hi)}
        </text>
        <text
          x={PAD_LEFT - 6}
          y={y(lo) + 3}
          textAnchor="end"
          className="fill-muted-foreground text-[9px] tabular-nums"
        >
          {format(lo)}
        </text>

        {previous.map((b) => (
          <g
            key={`prev-${b.col}`}
            fill="none"
            strokeDasharray="2 2"
            className="stroke-muted-foreground/70"
          >
            <line x1={x(b.col)} y1={y(b.q[0])} x2={x(b.col)} y2={y(b.q[4])} />
            <rect
              x={x(b.col) - BOX_W / 2}
              y={boxTop(b)}
              width={BOX_W}
              height={boxHeight(b)}
              rx={2}
            />
            <line
              x1={x(b.col) - BOX_W / 2}
              y1={y(b.q[2])}
              x2={x(b.col) + BOX_W / 2}
              y2={y(b.q[2])}
            />
          </g>
        ))}

        {boxes.map((b) => (
          <g key={b.col}>
            <title>{summary(b)}</title>
            <g className="stroke-muted-foreground" strokeWidth={1}>
              <line x1={x(b.col)} y1={y(b.q[0])} x2={x(b.col)} y2={y(b.q[4])} />
              <line
                x1={x(b.col) - CAP_W / 2}
                y1={y(b.q[4])}
                x2={x(b.col) + CAP_W / 2}
                y2={y(b.q[4])}
              />
              <line
                x1={x(b.col) - CAP_W / 2}
                y1={y(b.q[0])}
                x2={x(b.col) + CAP_W / 2}
                y2={y(b.q[0])}
              />
            </g>
            <rect
              x={x(b.col) - BOX_W / 2}
              y={boxTop(b)}
              width={BOX_W}
              height={boxHeight(b)}
              rx={2}
              strokeWidth={1.5}
              className="fill-chart-2/25 stroke-chart-2"
            />
            <line
              x1={x(b.col) - BOX_W / 2}
              y1={y(b.q[2])}
              x2={x(b.col) + BOX_W / 2}
              y2={y(b.q[2])}
              strokeWidth={2}
              className="stroke-chart-2"
            />
            {/* Transparent hit area so the tooltip covers the whole slot. */}
            <rect
              x={x(b.col) - SLOT_W / 2}
              y={PAD_TOP}
              width={SLOT_W}
              height={PLOT_H}
              fill="transparent"
            />
          </g>
        ))}
      </svg>
    </div>
  );
}
